import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Collapse,
  Paper,
  Breadcrumbs,
  TextField,
  InputAdornment,
  IconButton,
  Drawer,
  useMediaQuery,
  useTheme,
  Button,
  Fade,
  Monitor,
} from "@mui/material";
import {
  ExpandLess,
  ExpandMore,
  Search as SearchIcon,
  Settings as SettingsIcon,
  ChevronRight as ChevronRightIcon,
  Close as CloseIcon,
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
} from "@mui/icons-material";
import { settingsData } from "../../../config/settingsidebar";
import SettingsPageContainer from "../SettingPageContainer";

const SettingsLayout = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [expandedCategories, setExpandedCategories] = useState({});
  const [activeItem, setActiveItem] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);

  // Initialize expanded categories and active item based on current route
  useEffect(() => {
    const currentPath = location.pathname;
    const pathSegments = currentPath.split("/").filter(Boolean);

    if (pathSegments.length >= 3) {
      const category = pathSegments[1];

      setExpandedCategories((prev) => ({
        ...prev,
        [category]: true,
      }));
      setActiveItem(currentPath);
    } else if (pathSegments.length === 2 && pathSegments[0] === "settings") {
      // Default to first item of first category
      const firstCategory = settingsData[0];
      if (firstCategory?.children?.length > 0) {
        const defaultPath = firstCategory.children[0].link;
        navigate(defaultPath, { replace: true });
      }
    }
  }, [location.pathname, navigate]);

  const handleCategoryToggle = (categoryKey) => {
    setExpandedCategories((prev) => ({
      ...prev,
      [categoryKey]: !prev[categoryKey],
    }));
  };

  const handleItemClick = (link) => {
    setActiveItem(link);
    navigate(link);
    // Close mobile drawer when item is selected
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleCloseSidebar = () => {
    if (isMobile) {
      setMobileOpen(false);
    }
  };

  const handleBackToDashboard = () => {
    navigate("/dashboard");
  };
  const getBreadcrumbs = () => {
    const pathSegments = location.pathname.split("/").filter(Boolean);
    if (pathSegments.length < 3) return [];

    const categoryKey = pathSegments[1];
    const subcategoryKey = pathSegments[2];

    const category = settingsData.find((cat) => cat.key === categoryKey);
    const subcategory = category?.children?.find(
      (child) => child.key === subcategoryKey
    );

    return [
      { label: "Setup", path: "/schoolSettings" },
      { label: category?.label || categoryKey, path: null },
      { label: subcategory?.label || subcategoryKey, path: location.pathname },
    ];
  };

  // Filter settings based on search
  const filteredSettings = searchTerm
    ? settingsData
        .map((category) => ({
          ...category,
          children: category.children.filter(
            (child) =>
              child.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
              child.description
                ?.toLowerCase()
                .includes(searchTerm.toLowerCase())
          ),
        }))
        .filter((category) => category.children.length > 0)
    : settingsData;

  const breadcrumbs = getBreadcrumbs();

  // Sidebar Content Component
  const SidebarContent = () => (
    <Box
      sx={{
        height: "100%",
        display: "flex",
        flexDirection: "column",
        backgroundColor: "#084c84",
        color: "#ffffff",
      }}
    >
      {/* Header with Cross Button */}
      <Box
        sx={{
          p: 2,
          borderBottom: "1px solid rgba(255,255,255,0.1)",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <SettingsIcon sx={{ color: "#ffffff" }} />
          <Typography variant="h6" sx={{ fontWeight: 600, color: "#ffffff" }}>
            Setup
          </Typography>
        </Box>
        <IconButton
          onClick={handleCloseSidebar}
          sx={{
            color: "#ffffff",
            "&:hover": {
              backgroundColor: "rgba(255,255,255,0.1)",
              color: "#ff6b6b",
              transform: "rotate(90deg)",
            },
            transition: "all 0.3s ease",
          }}
          title="Close Sidebar"
        >
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Search */}
      <Box sx={{ p: 2, borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Search setup and configuration..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon
                  sx={{ color: "rgba(255,255,255,0.7)", fontSize: 20 }}
                />
              </InputAdornment>
            ),
            sx: {
              backgroundColor: "rgba(255,255,255,0.1)",
              color: "#ffffff",
              borderRadius: 1,
              "& .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "&:hover .MuiOutlinedInput-notchedOutline": {
                border: "none",
              },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                border: "1px solid #2789fd",
              },
              "& input::placeholder": {
                color: "rgba(255,255,255,0.7)",
                opacity: 1,
              },
            },
          }}
        />
      </Box>

      {/* Navigation List */}
      <Box
        sx={{
          flex: 1,
          overflow: "auto",
          "&::-webkit-scrollbar": {
            display: "none",
          },
          "-ms-overflow-style": "none",
          "scrollbar-width": "none",
        }}
      >
        <List disablePadding>
          {filteredSettings.map((category) => (
            <React.Fragment key={category.key}>
              {/* Category Header */}
              <ListItem disablePadding>
                <ListItemButton
                  onClick={() => handleCategoryToggle(category.key)}
                  sx={{
                    py: 1.5,
                    px: 2,
                    color: "#ffffff",
                    "&:hover": {
                      backgroundColor: "rgba(255,255,255,0.1)",
                      "& .MuiTypography-root": {
                        color: "#2789fd",
                      },
                      "& .MuiSvgIcon-root": {
                        color: "#2789fd",
                      },
                    },
                    transition: "all 0.3s ease",
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    <Typography variant="h6" sx={{ fontSize: "16px" }}>
                      {category.icon}
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 600,
                        fontSize: "13px",
                        textTransform: "uppercase",
                        letterSpacing: "0.5px",
                        color: "rgba(255,255,255,0.9)",
                        flex: 1,
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {category.label}
                    </Typography>
                  </Box>
                  {expandedCategories[category.key] ? (
                    <ExpandLess sx={{ color: "rgba(255,255,255,0.7)" }} />
                  ) : (
                    <ExpandMore sx={{ color: "rgba(255,255,255,0.7)" }} />
                  )}
                </ListItemButton>
              </ListItem>

              {/* Category Items */}
              <Collapse
                in={expandedCategories[category.key]}
                timeout="auto"
                unmountOnExit
              >
                <List disablePadding>
                  {category.children.map((item) => (
                    <ListItem key={item.key} disablePadding>
                      <ListItemButton
                        onClick={() => handleItemClick(item.link)}
                        selected={activeItem === item.link}
                        sx={{
                          py: 1,
                          px: 4,
                          color:
                            activeItem === item.link
                              ? "#084c84"
                              : "rgba(255,255,255,0.8)",
                          backgroundColor:
                            activeItem === item.link
                              ? "#ffffff"
                              : "transparent",
                          borderRadius: activeItem === item.link ? "10px" : "0",
                          margin: activeItem === item.link ? "5px 10px" : "0",
                          position: "relative",
                          "&:hover": {
                            backgroundColor:
                              activeItem === item.link
                                ? "#ffffff"
                                : "rgba(255,255,255,0.1)",
                            color:
                              activeItem === item.link ? "#084c84" : "#2789fd",
                          },
                          "&.Mui-selected": {
                            backgroundColor: "#ffffff",
                            color: "#084c84",
                            "&:hover": {
                              backgroundColor: "#ffffff",
                              color: "#084c84",
                            },
                          },
                          transition: "all 0.3s ease",
                        }}
                      >
                        <ListItemText
                          primary={item.label}
                          secondary={item.description}
                          primaryTypographyProps={{
                            fontSize: "14px",
                            fontWeight: activeItem === item.link ? 600 : 400,
                          }}
                          secondaryTypographyProps={{
                            fontSize: "12px",
                            color:
                              activeItem === item.link
                                ? "rgba(8, 76, 132, 0.7)"
                                : "rgba(255,255,255,0.6)",
                          }}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Collapse>
            </React.Fragment>
          ))}
        </List>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        display: "flex",
        height: "calc(100vh - 95px)", // Total viewport minus header (70px) and bottom strip (25px)
        backgroundColor: "#f8f9fa",
      }}
    >
      {/* Desktop Sidebar */}
      {!isMobile && (
        <Paper
          elevation={0}
          sx={{
            width: 280,
            height: "100%", // Take full height of parent
            flexShrink: 0,
            backgroundColor: "#084c84",
            color: "#ffffff",
            display: "flex",
            flexDirection: "column",
            position: "relative",
            borderRadius: "0 ",
            overflow: "hidden", // Prevent any overflow
            "& *": {
              "&::-webkit-scrollbar": {
                display: "none",
              },
              "-ms-overflow-style": "none",
              "scrollbar-width": "none",
            },
          }}
        >
          <SidebarContent />
        </Paper>
      )}

      {/* Mobile Drawer */}
      {isMobile && (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              boxSizing: "border-box",
              width: 280,
              backgroundColor: "#084c84",
              color: "#ffffff",
              "& *": {
                "&::-webkit-scrollbar": {
                  display: "none",
                },
                "-ms-overflow-style": "none",
                "scrollbar-width": "none",
              },
            },
          }}
        >
          <SidebarContent />
        </Drawer>
      )}

      {/* Main Content */}
      <Box
        sx={{
          flex: 1,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Mobile Header with Hamburger */}
        {isMobile && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderBottom: "1px solid #e1e5e9",
              backgroundColor: "#ffffff",
              borderRadius: 0,
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            <IconButton
              color="inherit"
              aria-label="open drawer"
              edge="start"
              onClick={handleDrawerToggle}
              sx={{
                color: "#084c84",
                "&:hover": {
                  backgroundColor: "rgba(8, 76, 132, 0.1)",
                },
              }}
            >
              <MenuIcon />
            </IconButton>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <SettingsIcon sx={{ color: "#084c84" }} />
              <Typography
                variant="h6"
                sx={{ color: "#084c84", fontWeight: 600 }}
              >
                Setup
              </Typography>
            </Box>
          </Paper>
        )}

        {/* Breadcrumb Header */}
        {breadcrumbs.length > 0 && (
          <Paper
            elevation={0}
            sx={{
              p: 2,
              borderBottom: "1px solid #e1e5e9",
              backgroundColor: "#ffffff",
              borderRadius: 0,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Breadcrumbs
              separator={<ChevronRightIcon fontSize="small" />}
              sx={{
                "& .MuiBreadcrumbs-separator": {
                  color: "#6c757d",
                },
              }}
            >
              {breadcrumbs.map((crumb, index) => (
                <Typography
                  key={index}
                  color={
                    index === breadcrumbs.length - 1
                      ? "primary"
                      : "text.secondary"
                  }
                  sx={{
                    fontSize: "14px",
                    fontWeight: index === breadcrumbs.length - 1 ? 600 : 400,
                    cursor: crumb.path ? "pointer" : "default",
                  }}
                  onClick={() => crumb.path && navigate(crumb.path)}
                >
                  {crumb.label}
                </Typography>
              ))}
            </Breadcrumbs>
          </Paper>
        )}

        {/* Page Content */}
        <Box
          sx={{
            flex: 1,
            overflow: "hidden",
            backgroundColor: "#ffffff",
            display: "flex",
            flexDirection: "column",
          }}
        >
          <SettingsPageContainer />
        </Box>
      </Box>
    </Box>
  );
};

export default SettingsLayout;
